import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { dependenciesListName, myDocumentsListName } from "src/api/config";
import Loading from "src/components/Loading";
import { UserAvatar } from "src/components/ui/avatar/Avatar";
import { useFiltersContext } from "src/contexts/FilterContext";
import { useAppSelector } from "src/hooks/redux";
import { useGetSelect } from "src/hooks/redux-selectors";
import { selectGetStatus } from "src/modules/spItems/selectors";
import { CheckBoxListItem, UserListItem } from "src/types/main";
import { TooltipItem } from "src/utils/Tooltip";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { useFilterDependenciesOptimized } from "src/utils/restFunction";
import { twMerge } from "tailwind-merge";
import { EquipmentKeys, Keys, ReportFilterKeys, RestKeys, UserKeys } from "../../type";

export function CheckUsersList({
  keyItem,
  items,
  onChecked,
}: {
  keyItem: UserKeys;
  items: UserListItem[];
  onChecked: (item: UserListItem) => void;
}) {
  const { filters } = useFiltersContext();

  function handleChecked(item: UserListItem) {
    onChecked(item);
  }

  const result = items.map((item, i) => {
    let isChecked = !!filters[keyItem].find((f) => f.id === item.id);

    return { ...item, isChecked };
  });

  return (
    <FlexRow>
      <FlexCol className="gap-y-[15px] grow max-h-[350px] overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-lg scrollbar-thumb-[#D1D5DB]">
        {result.map((item, i) => {
          return (
            <CheckboxUserUI
              key={item.email}
              item={item}
              index={i}
              onChecked={handleChecked}
              isCheck={item.isChecked}
              disabled={false}
            />
          );
        })}
      </FlexCol>
    </FlexRow>
  );
}

export function CheckEquipmentsList({
  keyItem,
  items,
  onChecked,
}: {
  keyItem: EquipmentKeys;
  items: CheckBoxListItem[];
  onChecked: (item: CheckBoxListItem) => void;
}) {
  const status = useAppSelector((state) => selectGetStatus(state, myDocumentsListName));
  const { filters } = useFiltersContext();

  function handleChecked(item: CheckBoxListItem) {
    onChecked(item);
  }

  const result = items.map((item, i) => {
    let isChecked = !!filters[keyItem].find((f) => f.id === item.id);

    return { ...item, isChecked };
  });

  return (
    <FlexRow>
      <FlexCol className="gap-y-[15px] grow max-h-[350px] overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-lg scrollbar-thumb-[#D1D5DB]">
        {result.map((item, i) => {
          return (
            <CheckboxUI
              key={item.id}
              item={item}
              index={i}
              onChecked={handleChecked}
              isCheck={item.isChecked}
              disabled={status !== "IDLE"}
            />
          );
        })}
      </FlexCol>
    </FlexRow>
  );
}

export const CheckboxUserUI = ({
  item,
  onChecked,
  isCheck,
  disabled,
  index,
}: {
  item: UserListItem;
  onChecked: (item: UserListItem) => void;
  isCheck: boolean;
  disabled: boolean;
  index: number;
}) => {
  const [isChecked, setIsChecked] = useState(isCheck);

  const handleCheckboxChange = () => {
    onChecked(item);
    setIsChecked((prev) => !prev);
  };

  if (disabled) return null;

  return (
    <label
      className={twMerge(disabled ? "select-none" : "cursor-pointer", "flex flex-row items-center w-full text-black")}
      aria-disabled={disabled}
      title={item.title}
    >
      <TooltipItem
        cursor={false}
        position={index > 1 ? "top-right" : "bottom-right"}
        tooltipsText={"Сбросьте другие фильтры, чтобы выбрать этот вариант"}
        disable={!disabled}
        className="w-64 translate-x-1"
        textClassName="whitespace-wrap"
      >
        <FlexRow>
          <FlexCol className="relative">
            <input
              type="checkbox"
              checked={isCheck}
              onChange={handleCheckboxChange}
              className="sr-only"
              disabled={disabled}
              placeholder=""
            />
            <div
              className={twMerge(
                disabled ? "cursor-not-allowed border-[#DFE4EA]" : "border-darkblue",
                "flex items-center justify-center w-5 h-5 mr-2 border rounded",
              )}
            >
              <span className={twMerge(isChecked ? "opacity-100" : "opacity-0")}>
                <svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                    fill={twMerge(disabled ? "#9CA3AF" : "#012169")}
                    stroke={twMerge(disabled ? "#9CA3AF" : "#012169")}
                    strokeWidth="0.4"
                  />
                </svg>
              </span>
            </div>
          </FlexCol>
          <FlexCol className="justify-center">
            <FlexRow className="gap-x-1 items-center">
              <UserAvatar
                avatar={item.avatar}
                displayName={item.title}
                email={item.email}
                shortName={item.shortName}
                className="w-4 h-4"
                classNameText="text-[10px] font-normal"
              />
              <FlexCol className={twMerge(disabled ? "text-[#9CA3AF]" : "text-darkblue", "line-clamp-1 ")}>
                {item.title}
              </FlexCol>
            </FlexRow>
          </FlexCol>
        </FlexRow>
      </TooltipItem>
    </label>
  );
};

export function CheckList({
  keyItem,
  items,
  onChecked,
}: {
  keyItem: Keys | RestKeys | ReportFilterKeys;
  items: CheckBoxListItem[];
  onChecked: (item: CheckBoxListItem) => void;
}) {
  const { filters, updateFilter } = useFiltersContext();

  const status = useAppSelector((state) => selectGetStatus(state, dependenciesListName));

  function handleChecked(item: CheckBoxListItem) {
    onChecked(item);
  }

  const dependencies = useGetSelect(dependenciesListName);

  const filterDependenciesOptimized = useFilterDependenciesOptimized();

  const computedFilters = useMemo(() => {
    return filterDependenciesOptimized(
      _.pick(filters, ["collections", "departments", "processes", "sectors", "types"]),
      dependencies.data,
    );
  }, [filters, dependencies.data, filterDependenciesOptimized]);

  const newFilters = _.cloneDeep(filters);

  const listWithDisable = items.map((item) => {
    const isDisabled =
      computedFilters[keyItem as Keys] && computedFilters[keyItem as Keys].length > 0
        ? !computedFilters[keyItem as Keys].some((f) => f.id === item.id)
        : false || false;

    if (isDisabled) {
      newFilters[keyItem] = newFilters[keyItem].filter((f) => f.id !== item.id);
    }

    return { ...item, isDisabled: isDisabled };
  });

  const listWithDisableAndCheck = listWithDisable.map((item) => {
    let isChecked = !!filters[keyItem].find((f) => f.id === item.id);

    return { ...item, isChecked };
  });

  const sortedItems = useMemo(
    () => listWithDisableAndCheck.sort((a, b) => Number(a.isDisabled) - Number(b.isDisabled)),
    [listWithDisableAndCheck],
  );

  useEffect(() => {
    updateFilter(newFilters);
  }, [JSON.stringify(newFilters)]); //* временный предохранитель

  return (
    <FlexRow>
      <FlexCol
        className={twMerge(
          "gap-y-[15px] grow max-h-[350px] overflow-y-auto scrollbar-none scrollbar-w-2 scrollbar-thumb-rounded-lg scrollbar-thumb-[#D1D5DB]"
        )}
      >
        {status === "LOADING" ? (
          <Loading />
        ) : (
          sortedItems.map((item, i) => {
            return (
              <CheckboxUI
                key={item.id}
                item={item}
                index={i}
                onChecked={handleChecked}
                isCheck={item.isChecked}
                disabled={item.isDisabled}
              />
            );
          })
        )}
      </FlexCol>
    </FlexRow>
  );
}

export const CheckboxUI = ({
  item,
  onChecked,
  isCheck,
  disabled,
  index,
}: {
  item: CheckBoxListItem;
  onChecked: (item: CheckBoxListItem) => void;
  isCheck: boolean;
  disabled: boolean;
  index: number;
}) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = () => {
    onChecked({ id: item.id, title: item.title, rest: item });
    setIsChecked((prev) => !prev);
  };

  useEffect(() => {
    setIsChecked(isCheck);
  }, [isCheck]);

  if (disabled) return null;

  return (
    <label
      className={twMerge(
        disabled ? "appearance-none select-none" : "cursor-pointer",
        "flex flex-row items-center w-full text-black",
      )}
      aria-disabled={disabled}
    >
      <TooltipItem
        cursor={false}
        position={index > 1 ? "top-right" : "bottom-right"}
        tooltipsText={"Сбросьте другие фильтры, чтобы выбрать этот вариант"}
        disable={!disabled}
        className="w-64 translate-x-1"
        textClassName={"whitespace-wrap"}
      >
        <FlexRow>
          <FlexCol className="relative">
            <input
              type="checkbox"
              checked={isCheck}
              onChange={handleCheckboxChange}
              className="sr-only"
              disabled={disabled}
            />
            <div
              className={twMerge(
                disabled ? "cursor-not-allowed border-[#DFE4EA]" : "border-darkblue",
                "flex items-center justify-center w-5 h-5 mr-2 border rounded",
              )}
            >
              <span className={twMerge(isChecked ? "opacity-100" : "opacity-0")}>
                <svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                    fill={twMerge(disabled ? "#9CA3AF" : "#012169")}
                    stroke={twMerge(disabled ? "#9CA3AF" : "#012169")}
                    strokeWidth="0.4"
                  />
                </svg>
              </span>
            </div>
          </FlexCol>
          <FlexCol className={twMerge("break-words", disabled ? "text-[#9CA3AF]" : "")}>{item.title}</FlexCol>
        </FlexRow>
      </TooltipItem>
    </label>
  );
};

export function TGCheckUsersList({
  list,
  items,
  onChecked,
}: {
  list: UserListItem[];
  items: UserListItem[];
  onChecked: (item: UserListItem) => void;
}) {
  function handleChecked(item: UserListItem) {
    onChecked(item);
  }

  const result = list.map((item, i) => {
    let isChecked = !!items.find((f) => f.id === item.id);

    return { ...item, isChecked };
  });

  return (
    <FlexRow>
      <FlexCol className="gap-y-[15px] grow max-h-[350px] overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-lg scrollbar-thumb-[#D1D5DB]">
        {result.map((item, i) => {
          return (
            <CheckboxUserUI
              key={item.email}
              item={item}
              index={i}
              onChecked={handleChecked}
              isCheck={item.isChecked}
              disabled={false}
            />
          );
        })}
      </FlexCol>
    </FlexRow>
  );
}

function toggleSortedList(
  list: (CheckBoxListItem & { isChecked: boolean })[],
  handleChecked: (item: CheckBoxListItem) => void,
  isCheckedSort: boolean = false,
) {
  return list
    .slice() // Создаём копию списка, чтобы не мутировать оригинальный
    .sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled)) // Сортировка по disabled
    .sort((a, b) => (isCheckedSort ? Number(b.isChecked) - Number(a.isChecked) : 0)) // Условная сортировка по isChecked
    .map((item, i) => {
      return (
        <CheckboxUI
          key={item.id}
          item={item}
          index={i}
          onChecked={handleChecked}
          isCheck={item.isChecked}
          disabled={!!item.disabled}
        />
      );
    });
}

export function TGCheckList({
  list,
  items,
  onChecked,
  isCheckedSort = false,
  loading = false,
}: {
  list: CheckBoxListItem[];
  items: CheckBoxListItem[];
  onChecked: (item: CheckBoxListItem) => void;
  isCheckedSort?: boolean;
  loading?: boolean;
}) {
  function handleChecked(item: CheckBoxListItem) {
    onChecked(item);
  }

  const result = list.map((item, i) => {
    let isChecked = !!items.find((f) => f.id === item.id);

    return { ...item, isChecked };
  });

  return (
    <FlexRow>
      <FlexCol className="gap-y-[15px] grow max-h-[350px] overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-lg scrollbar-thumb-[#D1D5DB]">
        {loading ? <Loading /> : toggleSortedList(result, handleChecked, isCheckedSort)}
      </FlexCol>
    </FlexRow>
  );
}
